import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { AlertService } from 'src/app/shared/services/alert.service';
import { CmsPaginationService } from 'src/app/shared/services/cms-pagination.service';
import { ITopicWithTranslations } from 'src/app/core/models/i-topic-with-translations';
import { IDeliveryTarget } from 'src/app/core/models/i-delivery-target';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';

@Component({
  selector: 'app-topic-management',
  templateUrl: './topic-management.component.html',
})
export class TopicManagementComponent implements OnInit, OnDestroy {
  constructor(
    private topicService: TopicService,
    private deliveryTargetsService: DeliveryTargetsService,
    private dialogService: DialogService,
    private alertService: AlertService,
    public cmsPaginationService: CmsPaginationService,
  ) {}

  subscriptions: Subscription[] = [];
  topics: ITopicWithTranslations[] = [];
  topicsById = new Map<number, ITopicWithTranslations>();
  dataSource = new MatTableDataSource<ITopicWithTranslations>();
  deliveryTargets: IDeliveryTarget[] = [];
  channelFilter = new UntypedFormControl('');
  searchControl = new UntypedFormControl('');
  pageSizeFilter = new UntypedFormControl();
  selectedPageSize = '25';
  pagingOptions: string[] = ['10', '25', '50', '100'];
  displayedColumns: string[] = ['Name', 'Parent', 'Sites', 'Actions'];
  debounceTime = 500;
  private topicsSubscription: Subscription;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  ngOnInit(): void {
    this.loadDeliveryTargets();
    this.loadTopicLookup();
    this.loadTopics();

    const channelFilterSubscription = this.channelFilter.valueChanges.subscribe(
      () => this.loadTopics(),
    );

    const searchSubscription = this.searchControl.valueChanges
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe(() => this.loadTopics());

    const pageSizeFilterSubscription = this.pageSizeFilter.valueChanges.subscribe(
      (value) => {
        this.cmsPaginationService.selectPageSize(this.dataSource, value);
        this.selectedPageSize = value;
      },
    );

    this.subscriptions.push(
      channelFilterSubscription,
      searchSubscription,
      pageSizeFilterSubscription,
    );
  }

  loadDeliveryTargets(): void {
    const deliveryTargetsSubscription = this.deliveryTargetsService
      .getAll$()
      .subscribe(
        (targets) => (this.deliveryTargets = targets),
        () =>
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the delivery targets. Please contact IT.',
          }),
      );
    this.subscriptions.push(deliveryTargetsSubscription);
  }

  loadTopicLookup(): void {
    const lookupSubscription = this.topicService.getFiltered$().subscribe(
      (topics) => {
        this.topicsById = new Map(
          (topics ?? []).map((topic) => [topic.id, topic]),
        );
      },
      () => {},
    );
    this.subscriptions.push(lookupSubscription);
  }

  loadTopics(): void {
    const channel = this.channelFilter.value || null;
    const searchText = this.searchControl.value || null;

    this.topicsSubscription?.unsubscribe();
    this.topicsSubscription = this.topicService
      .getFiltered$(channel, searchText)
      .subscribe(
        (topics) => {
          const childTopics = (topics ?? []).filter(
            (topic) => topic.parentId != null,
          );
          this.dataSource.paginator = this.paginator;
          this.dataSource.data = childTopics;
          this.topics = childTopics;
          this.cmsPaginationService.populatePageButtonArray(this.dataSource);
          this.pageSizeFilter.setValue(this.selectedPageSize);
        },
        () => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the topics. Please contact IT.',
          });
        },
      );
    this.subscriptions.push(this.topicsSubscription);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  getParentName(topic: ITopicWithTranslations): string {
    const parent =
      topic.parentId != null ? this.topicsById.get(topic.parentId) : undefined;
    return parent?.displayName ?? '';
  }

  deleteTopic(topic: ITopicWithTranslations): void {
    const confirmSubscription = this.dialogService
      .openConfirmationDialog({
        title: 'Delete Topic',
        message: `You are about to delete the topic "${topic.name}". Would you like to proceed?`,
        confirmLabel: 'Delete',
      })
      .afterClosed()
      .pipe(filter((dialogResponse) => !!dialogResponse))
      .subscribe(() => {
        const deleteSubscription = this.topicService.delete$(topic.id).subscribe(
          () => {
            this.alertService.success(
              'Topic: ' + topic.name + ' deleted successfully',
            );
            this.loadTopicLookup();
            this.loadTopics();
          },
          (error) => {
            this.dialogService.openErrorDialog({
              message: this.getErrorMessage(
                error,
                'An error has occurred whilst deleting the topic. Please contact IT.',
              ),
            });
          },
        );
        this.subscriptions.push(deleteSubscription);
      });
    this.subscriptions.push(confirmSubscription);
  }

  getErrorMessage(error: { error?: unknown }, fallback: string): string {
    return typeof error?.error === 'string' && error.error
      ? error.error
      : fallback;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
